import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Code,
  Copy,
  Check,
  Mail,
  Send,
  ExternalLink,
  Shield,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  Eye,
  Calculator,
  MessageCircle,
  HelpCircle,
  Headphones,
  Bot,
} from 'lucide-react';
import { toast } from 'sonner';

interface BotDeployProps {
  botId: string;
  tenantId: string;
  botStatus: string | null;
  widgetSettings?: {
    primary_color?: string;
    icon_style?: string;
    position?: string;
    greeting?: string;
  };
}

interface TenantInfo {
  name: string;
  developer_email: string | null;
  primary_domain: string | null;
}

interface DeployEmail {
  id: string;
  recipient_email: string;
  recipient_name: string | null;
  subject: string;
  status: string | null;
  sent_at: string | null;
  created_at: string | null;
}

const API_ENDPOINT = 'https://obdggmcxrswxhpoozqcl.supabase.co/functions/v1/advisory-chat';
const CDN_SCRIPT_URL = 'https://cdn.thinktomorrow.ai/widget.js';

const getWidgetIcon = (iconStyle: string | undefined) => {
  switch (iconStyle) {
    case 'calculator':
      return Calculator;
    case 'help':
      return HelpCircle;
    case 'headphones':
      return Headphones;
    case 'bot':
      return Bot;
    case 'chat':
    default:
      return MessageCircle;
  }
};

const BotDeploy: React.FC<BotDeployProps> = ({ botId, tenantId, botStatus, widgetSettings }) => {
  const { user } = useAdminAuth();
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [deployEmails, setDeployEmails] = useState<DeployEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedSnippet, setCopiedSnippet] = useState(false);
  const [copiedCSP, setCopiedCSP] = useState(false);
  
  // Email composer state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);

  const isLive = botStatus === 'live';

  // Generate embed snippet
  const embedSnippet = `<!-- Advisory Assistant Widget -->
<script
  src="${CDN_SCRIPT_URL}"
  data-bot-id="${botId}"
  data-api="${API_ENDPOINT}"
  async
></script>`;

  // CSP allowlist lines
  const cspLines = `script-src 'self' ${new URL(CDN_SCRIPT_URL).origin};
connect-src 'self' ${new URL(API_ENDPOINT).origin};
frame-src 'self' ${new URL(API_ENDPOINT).origin};`;

  // Install instructions template
  const installInstructions = `## Installation Instructions

### Step 1: Add the Widget Script

Add the following code snippet just before the closing </body> tag on every page where you want the assistant to appear:

\`\`\`html
${embedSnippet}
\`\`\`

### Step 2: Update Content Security Policy (CSP)

If your website uses a Content Security Policy, add these directives:

\`\`\`
${cspLines}
\`\`\`

### Step 3: Test

1. Clear your browser cache
2. Refresh your website
3. Look for the chat widget in the bottom-right corner
4. Click to open and test the assistant

### Need Help?

Contact our support team if you encounter any issues with the installation.`;

  useEffect(() => {
    fetchData();
  }, [botId, tenantId]);

  const fetchData = async () => {
    try {
      const [tenantRes, emailsRes] = await Promise.all([
        supabase.from('advisory_tenants').select('name, developer_email, website_url').eq('id', tenantId).single(),
        supabase.from('advisory_deploy_emails').select('*').eq('bot_id', botId).order('created_at', { ascending: false }).limit(10),
      ]);

      if (tenantRes.data) {
        setTenant({
          name: tenantRes.data.name,
          developer_email: tenantRes.data.developer_email,
          primary_domain: tenantRes.data.website_url,
        });
      }
      if (emailsRes.data) {
        setDeployEmails(emailsRes.data);
      }
    } catch (error) {
      console.error('Error fetching deploy data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, type: 'snippet' | 'csp') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'snippet') {
        setCopiedSnippet(true);
        setTimeout(() => setCopiedSnippet(false), 2000);
      } else {
        setCopiedCSP(true);
        setTimeout(() => setCopiedCSP(false), 2000);
      }
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const openEmailComposer = () => {
    // Pre-populate email fields
    setEmailTo(tenant?.developer_email || '');
    setEmailSubject(`Widget Installation Instructions - ${tenant?.name || 'Your'} AI Assistant`);
    setEmailBody(`Hi,

Your AI assistant is ready to be installed on your website.

${installInstructions}

Best regards,
ThinkTomorrow.AI Team`);
    setShowEmailDialog(true);
  };

  const handleSendEmail = async () => {
    if (!emailTo.trim()) {
      toast.error('Please enter a recipient email');
      return;
    }

    setIsSending(true);

    try {
      // Call edge function to send email
      const { data, error } = await supabase.functions.invoke('send-deploy-email', {
        body: {
          botId,
          tenantId,
          recipientEmail: emailTo.trim(),
          recipientName: tenant?.name || null,
          subject: emailSubject,
          body: emailBody,
          embedSnippet,
        },
      });

      if (error) throw error;

      // Log the deployment
      await supabase.from('advisory_deploy_emails').insert({
        bot_id: botId,
        tenant_id: tenantId,
        recipient_email: emailTo.trim(),
        recipient_name: tenant?.name || null,
        subject: emailSubject,
        body: emailBody,
        embed_snippet: embedSnippet,
        status: 'sent',
        sent_at: new Date().toISOString(),
        sent_by: user?.id,
      });

      toast.success('Installation email sent successfully');
      setShowEmailDialog(false);
      fetchData(); // Refresh deploy emails list
    } catch (error: any) {
      console.error('Error sending email:', error);
      
      // If email sending fails, still log with pending status and offer clipboard fallback
      try {
        await supabase.from('advisory_deploy_emails').insert({
          bot_id: botId,
          tenant_id: tenantId,
          recipient_email: emailTo.trim(),
          recipient_name: tenant?.name || null,
          subject: emailSubject,
          body: emailBody,
          embed_snippet: embedSnippet,
          status: 'failed',
          error_message: error.message || 'Failed to send',
          sent_by: user?.id,
        });
      } catch (logError) {
        console.error('Error logging deploy email:', logError);
      }

      toast.error('Failed to send email. Use "Copy to Clipboard" instead.', {
        description: error.message,
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleCopyEmailToClipboard = async () => {
    const fullContent = `To: ${emailTo}\nSubject: ${emailSubject}\n\n${emailBody}`;
    try {
      await navigator.clipboard.writeText(fullContent);
      
      // Log the copy action
      await supabase.from('advisory_deploy_emails').insert({
        bot_id: botId,
        tenant_id: tenantId,
        recipient_email: emailTo.trim() || 'clipboard',
        recipient_name: tenant?.name || null,
        subject: emailSubject,
        body: emailBody,
        embed_snippet: embedSnippet,
        status: 'copied',
        sent_by: user?.id,
      });

      toast.success('Email content copied to clipboard');
      setShowEmailDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {!isLive && (
        <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-medium text-amber-700">Bot Not Live</p>
            <p className="text-sm text-amber-600">
              The bot must be published to production before deploying to client websites.
            </p>
          </div>
        </div>
      )}

      {/* Embed Snippet Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="w-5 h-5" />
            Embed Snippet
          </CardTitle>
          <CardDescription>
            Add this code to your website to display the chat widget
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <pre className="p-4 bg-muted rounded-lg overflow-x-auto text-sm font-mono">
              {embedSnippet}
            </pre>
            <Button
              size="sm"
              variant="outline"
              className="absolute top-2 right-2"
              onClick={() => copyToClipboard(embedSnippet, 'snippet')}
            >
              {copiedSnippet ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </>
              )}
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Bot ID:</span>
              <code className="ml-2 px-2 py-1 bg-muted rounded font-mono text-xs">{botId}</code>
            </div>
            <div>
              <span className="text-muted-foreground">Status:</span>
              <Badge className={`ml-2 ${isLive ? 'bg-green-500/10 text-green-600' : 'bg-amber-500/10 text-amber-600'}`}>
                {isLive ? 'Ready to Deploy' : 'Not Live'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Widget Preview Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Widget Preview
          </CardTitle>
          <CardDescription>
            How the chat widget will appear on the client's website
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative border rounded-lg bg-gradient-to-br from-muted/30 to-muted min-h-[300px] overflow-hidden">
            {/* Mock browser chrome */}
            <div className="flex items-center gap-2 px-3 py-2 bg-muted border-b">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="flex-1 mx-2">
                <div className="bg-background rounded px-3 py-1 text-xs text-muted-foreground truncate">
                  {tenant?.primary_domain || 'example.com'}
                </div>
              </div>
            </div>
            
            {/* Mock website content */}
            <div className="p-6 space-y-4">
              <div className="h-6 w-32 bg-muted-foreground/20 rounded" />
              <div className="space-y-2">
                <div className="h-3 w-full bg-muted-foreground/20 rounded" />
                <div className="h-3 w-4/5 bg-muted-foreground/20 rounded" />
                <div className="h-3 w-3/5 bg-muted-foreground/20 rounded" />
              </div>
              <div className="h-20 w-full bg-muted-foreground/20 rounded" />
            </div>

            {/* Floating widget icon */}
            {(() => {
              const IconComponent = getWidgetIcon(widgetSettings?.icon_style);
              const position = widgetSettings?.position || 'bottom-right';
              const positionClasses = position === 'bottom-left' 
                ? 'bottom-4 left-4' 
                : 'bottom-4 right-4';
              
              return (
                <div 
                  className={`absolute ${positionClasses} flex flex-col items-end gap-2`}
                >
                  {/* Greeting tooltip */}
                  {showGreeting && widgetSettings?.greeting && (
                    <div className="max-w-[200px] p-3 bg-card rounded-lg shadow-lg border text-sm animate-in fade-in slide-in-from-bottom-2">
                      {widgetSettings.greeting}
                    </div>
                  )}
                  
                  {/* The launcher button */}
                  <button
                    onClick={() => setShowGreeting(!showGreeting)}
                    className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105"
                    style={{ backgroundColor: widgetSettings?.primary_color || '#14B8A6' }}
                  >
                    <IconComponent className="w-6 h-6 text-white" />
                  </button>
                </div>
              );
            })()}
          </div>

          {/* Widget settings summary */}
          <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Color:</span>
              <div className="flex items-center gap-2 mt-1">
                <div 
                  className="w-4 h-4 rounded border"
                  style={{ backgroundColor: widgetSettings?.primary_color || '#14B8A6' }}
                />
                <code className="text-xs">{widgetSettings?.primary_color || '#14B8A6'}</code>
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">Icon:</span>
              <p className="font-medium capitalize mt-1">{widgetSettings?.icon_style || 'chat'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Position:</span>
              <p className="font-medium capitalize mt-1">{(widgetSettings?.position || 'bottom-right').replace('-', ' ')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Install Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Installation Guide
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="instructions" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="instructions">Instructions</TabsTrigger>
              <TabsTrigger value="csp">CSP Allowlist</TabsTrigger>
            </TabsList>

            <TabsContent value="instructions" className="space-y-4">
              <div className="prose prose-sm max-w-none">
                <h4>Step 1: Add the Widget Script</h4>
                <p className="text-muted-foreground">
                  Add the embed snippet just before the closing <code>&lt;/body&gt;</code> tag on every page 
                  where you want the assistant to appear.
                </p>

                <h4>Step 2: Update CSP (if applicable)</h4>
                <p className="text-muted-foreground">
                  If your website uses a Content Security Policy, add the required directives from the CSP tab.
                </p>

                <h4>Step 3: Test</h4>
                <ol className="text-muted-foreground">
                  <li>Clear your browser cache</li>
                  <li>Refresh your website</li>
                  <li>Look for the chat widget in the bottom-right corner</li>
                </ol>
              </div>
            </TabsContent>

            <TabsContent value="csp" className="space-y-4">
              <p className="text-sm text-muted-foreground">
                If your website uses a Content Security Policy, add these directives to allow the widget:
              </p>
              <div className="relative">
                <pre className="p-4 bg-muted rounded-lg overflow-x-auto text-sm font-mono">
                  {cspLines}
                </pre>
                <Button
                  size="sm"
                  variant="outline"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(cspLines, 'csp')}
                >
                  {copiedCSP ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Email Composer Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Install Email Composer
          </CardTitle>
          <CardDescription>
            Send installation instructions to the developer
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm">
                <span className="text-muted-foreground">Developer Email:</span>{' '}
                <span className="font-medium">{tenant?.developer_email || 'Not configured'}</span>
              </p>
              {tenant?.primary_domain && (
                <p className="text-sm">
                  <span className="text-muted-foreground">Domain:</span>{' '}
                  <a 
                    href={`https://${tenant.primary_domain}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    {tenant.primary_domain}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
              )}
            </div>
            <Button onClick={openEmailComposer} disabled={!isLive}>
              <Mail className="w-4 h-4 mr-2" />
              Compose Email
            </Button>
          </div>

          {/* Recent Deploy Emails */}
          {deployEmails.length > 0 && (
            <>
              <Separator />
              <div>
                <p className="text-sm font-medium mb-3">Deployment Log</p>
                <div className="space-y-2">
                  {deployEmails.map((email) => (
                    <div 
                      key={email.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {email.status === 'sent' ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : email.status === 'copied' ? (
                          <Copy className="w-4 h-4 text-blue-600" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-destructive" />
                        )}
                        <div>
                          <p className="text-sm font-medium">{email.recipient_email}</p>
                          <p className="text-xs text-muted-foreground">
                            {email.status === 'sent' ? 'Sent' : email.status === 'copied' ? 'Copied' : 'Failed'} • {new Date(email.created_at || '').toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={
                          email.status === 'sent' 
                            ? 'bg-green-500/10 text-green-600 border-green-500/20'
                            : email.status === 'copied'
                            ? 'bg-blue-500/10 text-blue-600 border-blue-500/20'
                            : 'bg-destructive/10 text-destructive border-destructive/20'
                        }
                      >
                        {email.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Email Composer Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Compose Install Email</DialogTitle>
            <DialogDescription>
              Send installation instructions to the developer
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email-to">To</Label>
              <Input
                id="email-to"
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="developer@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email-subject">Subject</Label>
              <Input
                id="email-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email-body">Message</Label>
              <Textarea
                id="email-body"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                className="min-h-[300px] font-mono text-sm"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleCopyEmailToClipboard}>
              <Copy className="w-4 h-4 mr-2" />
              Copy to Clipboard
            </Button>
            <Button onClick={handleSendEmail} disabled={isSending}>
              {isSending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BotDeploy;
